<script setup lang="ts" generic="T">
import { useScreen } from "@/composable/useResponsive";
import Settings, {
  type ItemDisplayMode,
  type ListDisplayMode,
} from "@/stores/Settings";
import { computed } from "vue";
import SkeletonList from "../skeleton/SkeletonList.vue";
import DRContainer from "../DRContainer.vue";

const props = withDefaults(
  defineProps<{
    data?: Array<T>;
    loading?: boolean;
    emptyMessage?: string;
    keyIndex: keyof T;
    minWidth?: number;
    display?: ListDisplayMode;
  }>(),
  {
    loading: false,
    emptyMessage: "Vide",
    minWidth: 300,
  }
);

const { screen } = useScreen();

const display = computed(() => props.display || Settings.displayMode);
const displayItem = computed<ItemDisplayMode>(() => {
  // Force card/tile display on mobile (small screens)
  if (!screen.value.gt.sm) {
    return "card";
  }
  return display.value === "grid" ? "card" : "line";
});

const nbOfColumns = (containerWidth: number) => {
  const minElementSize = props.minWidth;
  if (containerWidth >= minElementSize * 6) return 6;
  if (containerWidth >= minElementSize * 4) return 4;
  if (containerWidth >= minElementSize * 3) return 3;
  if (containerWidth >= minElementSize * 2) return 2;
  return 1;
};

const gridClass = (containerWidth: number) => {
  if (display.value === "list") return "col-12";
  const cols = nbOfColumns(containerWidth);
  if (cols === 6) return "col-2";
  if (cols === 4) return "col-3";
  if (cols === 3) return "col-4";
  if (cols === 2) return "col-6";
  return "col-12";
};
</script>

<template>
  <div v-if="loading">
    <SkeletonList :count="10" height="3.5rem" />
  </div>

  <DRContainer v-else-if="data?.length">
    <template v-slot="{ width }">
      <div
        :class="
          display === 'grid' || screen.gt.sm
            ? 'row g-2'
            : 'd-flex flex-column gap-2'
        "
      >
        <div
          v-for="item in data"
          :key="(item[keyIndex] as PropertyKey)"
          :class="gridClass(width)"
        >
          <slot name="item" v-bind="{ item, display: displayItem }"></slot>
        </div>
      </div>
    </template>
  </DRContainer>

  <div v-else class=" ">
    <slot name="empty">
      <p class="py-5 fs-4 text-center text-grey">
        {{ emptyMessage }}
      </p></slot
    >
  </div>
</template>

<style lang="scss" scoped></style>
